import get from 'lodash.get';
import {
    VirtualizedTableItem,
    VirtualizedTableColumns
} from 'services/virtualizedTable/types';
import { selectUnitTypeOptions } from 'services/unitTypes/selectors';
import { makeSelectTableColumnWidths } from 'services/columnWidths/selectors';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import {
    FIELD_TYPE_STRING,
    FIELD_TYPE_NUMBER,
    FIELD_TYPE_DROPDOWN
} from 'components/VirtualizedTable/constants';
import { FertilizerProduct } from 'services/fertilizerProducts/types';
import { selectFertilizerProductsArray } from 'services/fertilizerProducts/selectors';

const selectActiveFertilizerProducts = createDeepEqualSelector(
    selectFertilizerProductsArray,
    (products) => products.filter((product) => !product.deletedAt)
);

const selectProductsTableColumns = createDeepEqualSelector(
    [makeSelectTableColumnWidths(), selectUnitTypeOptions],
    (
        tableColumnWidths,
        unitTypeOptions
    ): VirtualizedTableColumns<FertilizerProduct> => {
        const result: VirtualizedTableColumns<FertilizerProduct> = [
            {
                label: 'Name',
                value: 'name',
                width: 250,
                type: FIELD_TYPE_STRING,
                validators: 'isRequired',
                disableUpdating: true
            },
            {
                label: 'Witholding Period (days)',
                value: 'witholdingPeriodDays',
                width: 200,
                type: FIELD_TYPE_NUMBER,
                validators: [
                    'isNumber',
                    'isExactlyDivisibleByHalf',
                    'minValue0',
                    'maxValue999Half'
                ],
                emptyValueToNull: true
            },
            {
                label: 'Re-Entry Period (days)',
                value: 'reEntryPeriodDays',
                width: 200,
                type: FIELD_TYPE_NUMBER,
                validators: [
                    'isNumber',
                    'isExactlyDivisibleByHalf',
                    'minValue0',
                    'maxValue999Half'
                ],
                emptyValueToNull: true
            },
            {
                label: 'Unit Type',
                value: 'unitTypeId',
                width: 150,
                type: FIELD_TYPE_DROPDOWN,
                options: unitTypeOptions,
                validators: ['isRequiredNumber', 'isInteger']
            },
            {
                label: 'Nitrogen Value',
                value: 'nitrogenValue',
                width: 150,
                type: FIELD_TYPE_NUMBER,
                validators: [
                    'isNumber',
                    'minValue0',
                    'maxValue100',
                    'isRequiredNumber'
                ]
            },
            {
                label: 'Phosphorus Value',
                value: 'phosphorusValue',
                width: 150,
                type: FIELD_TYPE_NUMBER,
                validators: [
                    'isNumber',
                    'minValue0',
                    'maxValue100',
                    'isRequiredNumber'
                ]
            },
            {
                label: 'Potassium Value',
                value: 'potassiumValue',
                width: 150,
                type: FIELD_TYPE_NUMBER,
                validators: [
                    'isNumber',
                    'minValue0',
                    'maxValue100',
                    'isRequiredNumber'
                ]
            },
            {
                label: 'Sulfur Value',
                value: 'sulfurValue',
                width: 150,
                type: FIELD_TYPE_NUMBER,
                validators: ['isNumber', 'minValue0', 'maxValue100'],
                emptyValueToNull: true
            },
            {
                label: 'Magnesium Value',
                value: 'magnesiumValue',
                width: 150,
                type: FIELD_TYPE_NUMBER,
                validators: ['isNumber', 'minValue0', 'maxValue100'],
                emptyValueToNull: true
            },
            {
                label: 'Calcium Value',
                value: 'calciumValue',
                width: 150,
                type: FIELD_TYPE_NUMBER,
                validators: ['isNumber', 'minValue0', 'maxValue100'],
                emptyValueToNull: true
            }
        ];

        return result.map((column) => {
            const { width, value } = column;
            return {
                ...column,
                width: get(tableColumnWidths, value, width)
            };
        });
    }
);

const selectFertilizerProductItems = createDeepEqualSelector(
    selectActiveFertilizerProducts,
    (products) =>
        products.map(
            (product: FertilizerProduct): VirtualizedTableItem => ({
                ...product,
                key: product.uuid
            })
        )
);

const makeSelectFertilizerProductItems =
    (): typeof selectFertilizerProductItems => selectFertilizerProductItems;

export {
    selectProductsTableColumns,
    selectFertilizerProductItems,
    selectActiveFertilizerProducts,
    makeSelectFertilizerProductItems
};
